import { observer } from "mobx-react";
import { Box } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import LayoutContentWrapper from "../../components/layout/LayoutContentWrapper";
import LayoutContentToolbar from "../../components/layout/LayoutContentToolbar";
import FilledButton from "../../components/buttons/FilledButton";
import FormSection from "../../forms/FormSection";
import FormRow from "../../forms/FormRow";
import FormDateField from "../../components/fields/FormDateField";
import FormAutocompleteMultipleSelectField from "../../components/fields/FormAutocompleteMultipleSelectField";
import useFeature from "../../useFeature";
import { useApplication } from "../../ApplicationProvider";
import { useNavigate } from "react-router-dom";
import DashboardFeature, { MONEY, BOXES } from "./DashboardFeature";
import DashboardChartsGrid from "./DashboardChartsGrid";
import DashboardToggle from "./DashboardToggle";
import SummaryKpisSection from "./charts/SummaryKpisSection";
import RevenueBurnupChart from "./charts/RevenueBurnupChart";
import SalesByChannelChart from "./charts/SalesByChannelChart";
import SalesByArticleCategoryChart from "./charts/SalesByArticleCategoryChart";

const CHART_TITLES = {
    burnup: { [MONEY]: "Facturación acumulada vs presupuesto del período", [BOXES]: "Cajas acumuladas vs presupuesto del período" },
    channel: { [MONEY]: "Ventas por Canal", [BOXES]: "Cajas por Canal" },
    channelZoomedIntoCustomers: { [MONEY]: "Ventas por Cliente", [BOXES]: "Cajas por Cliente" },
    category: { [MONEY]: "Ventas por Categoría de Artículos", [BOXES]: "Cajas por Categoría de Artículos" },
    categoryZoomedIntoArticles: { [MONEY]: "Ventas por Artículo", [BOXES]: "Cajas por Artículo" },
};

const UNIT_AWARE_CHART_KEYS = ["burnup", "channel", "category"];

function DashboardScreen() {
    const application = useApplication();
    const navigator = useNavigate();
    const feature = useFeature(() => new DashboardFeature(application, navigator, UNIT_AWARE_CHART_KEYS));

    if (!feature) {
        return <LayoutContentWrapper isLoading renderContent={() => null} />;
    }

    const filters = feature.filtersForm().formEntity();

    const unitToggleFor = (chartKey) => (
        <DashboardToggle compact options={feature.units()} value={feature.unitFor(chartKey)} onChange={(unit) => feature.setChartUnit(chartKey, unit)} />
    );
    const chartTitleKeyFor = (chartKey) => {
        if (chartKey === "channel" && feature.zoomedChannel()) {
            return "channelZoomedIntoCustomers";
        }
        if (chartKey === "category" && feature.zoomedCategory()) {
            return "categoryZoomedIntoArticles";
        }
        return chartKey;
    };
    const titleFor = (chartKey) => CHART_TITLES[chartTitleKeyFor(chartKey)][feature.unitFor(chartKey)];

    const chartTiles = [
        {
            key: "burnup",
            title: titleFor("burnup"),
            layout: { x: 0, y: 0, w: 12, h: 8, minW: 4, minH: 5 },
            headerAction: unitToggleFor("burnup"),
            element: <RevenueBurnupChart filters={filters} boxesMode={feature.isBoxes("burnup")} />,
        },
        {
            key: "channel",
            title: titleFor("channel"),
            layout: { x: 0, y: 8, w: 12, h: 8, minW: 3, minH: 4 },
            scrollableX: true,
            headerAction: unitToggleFor("channel"),
            element: (
                <SalesByChannelChart
                    filters={filters}
                    boxesMode={feature.isBoxes("channel")}
                    zoomedChannel={feature.zoomedChannel()}
                    onZoomIntoChannel={(channel) => feature.zoomIntoChannel(channel)}
                    onExitChannelZoom={() => feature.exitChannelZoom()}
                />
            ),
        },
        {
            key: "category",
            title: titleFor("category"),
            layout: { x: 0, y: 16, w: 12, h: 8, minW: 3, minH: 4 },
            scrollableX: true,
            headerAction: unitToggleFor("category"),
            element: (
                <SalesByArticleCategoryChart
                    filters={filters}
                    boxesMode={feature.isBoxes("category")}
                    zoomedCategory={feature.zoomedCategory()}
                    onZoomIntoCategory={(category) => feature.zoomIntoCategory(category)}
                    onExitCategoryZoom={() => feature.exitCategoryZoom()}
                />
            ),
        },
    ];

    return (
        <LayoutContentWrapper
            isLoading={false}
            renderContent={() => (
                <Box>
                    <LayoutContentToolbar title="Dashboard Comercial">
                        <FilledButton onClick={() => feature.load()}>
                            <RefreshIcon fontSize="small" />
                            Refrescar
                        </FilledButton>
                    </LayoutContentToolbar>

                    <FormSection title="Filtros">
                        <FormRow>
                            <FormDateField label="Desde" field={feature.fromDateField()} />
                            <FormDateField label="Hasta" field={feature.toDateField()} />
                        </FormRow>
                        <FormRow>
                            <FormAutocompleteMultipleSelectField
                                label="Tipo de cliente"
                                field={feature.channelsField()}
                                options={feature.channelOptions()}
                            />
                            <FormAutocompleteMultipleSelectField
                                label="Categoría de Artículo"
                                field={feature.articleCategoriesField()}
                                options={feature.articleCategoryOptions()}
                            />
                        </FormRow>
                    </FormSection>

                    <SummaryKpisSection filters={filters} />

                    <DashboardChartsGrid
                        items={chartTiles}
                        leadingAction={
                            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
                                <DashboardToggle options={feature.units()} value={feature.globalUnit()} onChange={(unit) => feature.setAllUnits(unit)} />
                                <DashboardToggle options={feature.allowanceModes()} value={feature.withoutAllowancesField().value} onChange={(withoutAllowances) => feature.withoutAllowancesField().updateValue(withoutAllowances)} />
                            </Box>
                        }
                    />
                </Box>
            )}
        />
    );
}

export default observer(DashboardScreen);
